
import {ComputedRef, defineComponent, onMounted, Ref, ref, watch} from "vue";
import {pdfKey} from "../../../../src/main";
import store from "../../../repository/store/store";
import {dispatchError} from "../../utils/error_handling";
import {Content} from "./helpers";
import {ContentType} from "../../../../domain/entities/content/content";
import {computed} from "@vue/reactivity";
import ContentFallback from "../../components/application/moldure/ContentFallback.vue";
import {onBeforeRouteLeave} from "vue-router";

/** Interface used to define the implementations of the window object that contains the definitions of the AdobeDc API. */
declare global {
  interface Window {
    AdobeDC;
  }
}

type AdobeKeys = {
  clientId: string;
  divId: string
};

/** Type definition for the AdobeContent object. */
type AdobeContent = {
  location: {
    url: string;
  };
};

/** Type definition for the AdobeData object. */
type AdobeMetaData = {
  fileName: string;
};

type AdobeContentMetaData = {
  content: AdobeContent;
  metaData: AdobeMetaData;
};

type AdobeConfiguration = { showAnnotationTools: boolean };

/** Interface used to define the attributes of the AdobeDc object. */
interface AdobeDc {
  View(adobeKeys: AdobeKeys);

  previewFile(contentMetaData: AdobeContentMetaData, configuration: AdobeConfiguration);
}

export default defineComponent({
  name: "PDFViewer",
  components: {ContentFallback},
  setup() {

    const isPDFRendered: Ref<boolean> = ref(true);

    /** Load the AdobeDC PDF. */
    onMounted(async () => {
      try {
        await loadAdobeDCPDF();
        await startProgressRoutine();
      } catch (e) {
        console.error("[onMounted] Error ", e);
        await dispatchError(e);
      }
    });

    const currentContent: ComputedRef<Content> = computed(() => store.getters["content_module/currentContent"]);

    /** Reload the AdobeDC PDF. */
    watch(currentContent, async (newContent: Content) => {
      try {
        await stopProgressRoutine();
        if (newContent.type !== ContentType.PDF) return;
        await loadAdobeDCPDF();
        await startProgressRoutine();
      } catch (e) {
        console.error("[watch] Error ", e);
        await dispatchError(e);
      }
    });

    /** Used to know if the script is already loaded. */
    const scriptID = "adobe-dc-view-script";

    const loadAdobeDCPDF = async () => {
      try {
        const adobeDCViewScript = document.getElementById(scriptID);
        if (!adobeDCViewScript) {
          createAdobeDCViewScript();
          createAdobeDCViewListener();
        } else {
          await onAdobeDCViewScriptReady();
        }
      } catch (e) {
        console.error("[loadAdobeDCView] Error ", e);
        throw e;
      }
    };

    /** Load pdf api script and create event listener to render the pdf file. */
    const createAdobeDCViewScript = () => {
      const script = document.createElement("script");
      script.src = "https://acrobatservices.adobe.com/view-sdk/viewer.js";
      script.id = scriptID;
      document.head.appendChild(script);
    };

    /** Create the event listener responsible to render the PDF file when ready. */
    const createAdobeDCViewListener = () => {
      document.addEventListener("adobe_dc_view_sdk.ready", onAdobeDCViewScriptReady);
    };

    /** Render the AdobeDC PDF when the AdobeDCViewScript is ready. */
    const onAdobeDCViewScriptReady = async () => {
      try {
        await renderAdobeDCView();
      } catch (e) {
        console.error("[onAdobeDCViewScriptReady] Error ", e);
        await dispatchError(e, "Erro ao carregar o PDF");
        isPDFRendered.value = false;
      }
    };

    /** Render AdobeDCView PDF. */
    const renderAdobeDCView = async () => {
      if (!currentContent.value) throw new Error("Content is null");
      if (!currentContent.value.file) throw new Error("Content file is null");
      if (!currentContent.value.file.path) throw new Error("Content file path is empty");

      const adobeKeys: AdobeKeys = {clientId: pdfKey(), divId: "adobe-dc-view"};
      const adobeDCView: AdobeDc = new window.AdobeDC.View(adobeKeys);

      const content: AdobeContent = {location: {url: currentContent.value.file.path}};
      const metaData: AdobeMetaData = {fileName: currentContent.value.title};
      const contentMetaData: AdobeContentMetaData = {content, metaData}
      const configuration: AdobeConfiguration = {showAnnotationTools: false};

      await adobeDCView.previewFile(contentMetaData, configuration);
    };

    const startProgressRoutine = async () => {
      try {
        await store.dispatch("content_module/player/progress/startProgressRoutine");
      } catch (e) {
        console.log("[startProgressRoutine] Error ", e);
        throw e;
      }
    };

    const stopProgressRoutine = async () => {
      try {
        await store.dispatch("content_module/player/progress/stopProgressRoutine");
      } catch (e) {
        console.log("[startProgressRoutine] Error ", e);
        throw e;
      }
    };

    onBeforeRouteLeave(async () => {
      try {
        await stopProgressRoutine();
      } catch (e) {
        console.log("[onBeforeRouteLeave] Error ", e);
        throw e;
      }
    });

    return {
      isPDFRendered,
    }
  }
});
